/**
 * Secrets API — CRUD operations for application secrets stored in Vault.
 *
 * Endpoints:
 *   GET    /api/v1/applications/:alias/secrets          — list secret keys
 *   PUT    /api/v1/applications/:alias/secrets/:key      — create/update secret
 *   GET    /api/v1/applications/:alias/secrets/:key/reveal — reveal secret value
 *   DELETE /api/v1/applications/:alias/secrets/:key      — delete secret
 *
 * Also includes ApplicationResource endpoints for resource-to-app wiring:
 *   GET    /api/v1/applications/:alias/resources          — list attached resources
 *   POST   /api/v1/applications/:alias/resources          — attach resource
 *   DELETE /api/v1/applications/:alias/resources/:localAlias — detach resource
 */

import { apiBaseUrls } from "src/platform/constants/shared-constants";
import API from "src/platform/http/shared-http";
import debugLogger from "src/platform/utils/debugLogger";

export interface SecretMetadata {
  id: number;
  key: string;
  isSecret: boolean;
  hint: string | null;
  applicationId: number;
  organizationId: number;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface SecretRevealResponse {
  key: string;
  value: string;
}

export interface ApplicationResourceRecord {
  id: number;
  alias: string;
  resource: {
    id: number;
    alias: string;
    resourceType: string;
    status: string;
    endpoint: string | null;
    credentialsRef: string | null;
  };
}

export interface AttachResourceRequest {
  resourceAlias: string;
  alias: string;
}

const BASE = "/api/v1/applications";

export async function listSecrets(appAlias: string): Promise<SecretMetadata[]> {
  debugLogger.info("secrets-api", `Listing secrets for ${appAlias}`);
  const api = new API(apiBaseUrls.BACKEND, `${BASE}/${appAlias}/secrets`);
  return api.get<SecretMetadata[]>();
}

export async function upsertSecret(
  appAlias: string,
  key: string,
  value: string,
): Promise<SecretMetadata> {
  debugLogger.info("secrets-api", `Upserting secret ${key} for ${appAlias}`);
  const api = new API(
    apiBaseUrls.BACKEND,
    `${BASE}/${appAlias}/secrets/${encodeURIComponent(key)}`,
  );
  return api.put<
    SecretMetadata,
    { key: string; value: string; isSecret: boolean }
  >({
    key,
    value,
    isSecret: true,
  });
}

export async function revealSecret(
  appAlias: string,
  key: string,
): Promise<string> {
  debugLogger.info("secrets-api", `Revealing secret ${key} for ${appAlias}`);
  const api = new API(
    apiBaseUrls.BACKEND,
    `${BASE}/${appAlias}/secrets/${encodeURIComponent(key)}/reveal`,
  );
  const result = await api.get<SecretRevealResponse>();
  return result.value;
}

export async function deleteSecret(
  appAlias: string,
  key: string,
): Promise<void> {
  debugLogger.info("secrets-api", `Deleting secret ${key} for ${appAlias}`);
  const api = new API(
    apiBaseUrls.BACKEND,
    `${BASE}/${appAlias}/secrets/${encodeURIComponent(key)}`,
  );
  await api.delete<void>();
}

export async function listAppResources(
  appAlias: string,
): Promise<ApplicationResourceRecord[]> {
  debugLogger.info("secrets-api", `Listing resources for ${appAlias}`);
  const api = new API(apiBaseUrls.BACKEND, `${BASE}/${appAlias}/resources`);
  return api.get<ApplicationResourceRecord[]>();
}

export async function attachResource(
  appAlias: string,
  request: AttachResourceRequest,
): Promise<ApplicationResourceRecord> {
  debugLogger.info("secrets-api", `Attaching resource to ${appAlias}`, request);
  const api = new API(apiBaseUrls.BACKEND, `${BASE}/${appAlias}/resources`);
  return api.post<ApplicationResourceRecord, AttachResourceRequest>(request);
}

export async function detachResource(
  appAlias: string,
  localAlias: string,
): Promise<void> {
  debugLogger.info(
    "secrets-api",
    `Detaching resource ${localAlias} from ${appAlias}`,
  );
  const api = new API(
    apiBaseUrls.BACKEND,
    `${BASE}/${appAlias}/resources/${encodeURIComponent(localAlias)}`,
  );
  await api.delete<void>();
}

export interface ResourceAppRecord {
  id: number;
  alias: string;
  resource: {
    id: number;
    alias: string;
    resourceType: string;
    status: string;
    endpoint: string | null;
    credentialsRef: string | null;
  };
  application: {
    id: number;
    alias: string;
    name: string;
    status: string;
  };
}

export async function listAppsByResource(
  resourceAlias: string,
): Promise<ResourceAppRecord[]> {
  debugLogger.info("secrets-api", `Listing apps for resource ${resourceAlias}`);
  const api = new API(
    apiBaseUrls.BACKEND,
    `/api/v1/resources/${encodeURIComponent(resourceAlias)}/applications`,
  );
  return api.get<ResourceAppRecord[]>();
}

// ── Scope-parameterized secrets (org / project / effective) ───────────────────

/**
 * Scoped secret metadata returned by the org/project scope endpoints.
 * Unlike stack-scope SecretMetadata, these carry a `scope` field and no
 * applicationId (org/project secrets are not tied to a single application).
 */
export interface ScopedSecretMetadata {
  id: number;
  key: string;
  isSecret: boolean;
  /** "org" | "project" | "stack" */
  scope: string;
  /** Safe metadata only; the secret value is never included here. */
  reference: {
    vaultPath: string;
    property: string;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Effective merged secret preview for a stack — key names + source scope.
 * `overriddenBy` is null, or "stack" when a stack-scope secret overrides
 * the inherited key.
 */
export interface EffectiveSecret {
  key: string;
  sourceScope: "org" | "project" | "stack";
  overriddenBy: string | null;
}

const ORG_BASE = "/api/v1/organizations";
const PROJECT_BASE = "/api/v1/projects";

// ── Org-scope secrets ─────────────────────────────────────────────────────────

export async function listOrgSecrets(
  orgAlias: string,
): Promise<ScopedSecretMetadata[]> {
  debugLogger.info("secrets-api", `Listing org secrets for ${orgAlias}`);
  const api = new API(apiBaseUrls.BACKEND, `${ORG_BASE}/${orgAlias}/secrets`);
  return api.get<ScopedSecretMetadata[]>();
}

export async function upsertOrgSecret(
  orgAlias: string,
  key: string,
  value: string,
): Promise<ScopedSecretMetadata> {
  debugLogger.info(
    "secrets-api",
    `Upserting org secret ${key} for ${orgAlias}`,
  );
  const api = new API(
    apiBaseUrls.BACKEND,
    `${ORG_BASE}/${orgAlias}/secrets/${encodeURIComponent(key)}`,
  );
  return api.put<
    ScopedSecretMetadata,
    { key: string; value: string; isSecret: boolean }
  >({
    key,
    value,
    isSecret: true,
  });
}

export async function revealOrgSecret(
  orgAlias: string,
  key: string,
): Promise<string> {
  debugLogger.info(
    "secrets-api",
    `Revealing org secret ${key} for ${orgAlias}`,
  );
  const api = new API(
    apiBaseUrls.BACKEND,
    `${ORG_BASE}/${orgAlias}/secrets/${encodeURIComponent(key)}/reveal`,
  );
  const result = await api.get<SecretRevealResponse>();
  return result.value;
}

export async function deleteOrgSecret(
  orgAlias: string,
  key: string,
): Promise<void> {
  debugLogger.info("secrets-api", `Deleting org secret ${key} for ${orgAlias}`);
  const api = new API(
    apiBaseUrls.BACKEND,
    `${ORG_BASE}/${orgAlias}/secrets/${encodeURIComponent(key)}`,
  );
  await api.delete<void>();
}

// ── Project-scope secrets ─────────────────────────────────────────────────────

export async function listProjectSecrets(
  projectAlias: string,
): Promise<ScopedSecretMetadata[]> {
  debugLogger.info(
    "secrets-api",
    `Listing project secrets for ${projectAlias}`,
  );
  const api = new API(
    apiBaseUrls.BACKEND,
    `${PROJECT_BASE}/${projectAlias}/secrets`,
  );
  return api.get<ScopedSecretMetadata[]>();
}

export async function upsertProjectSecret(
  projectAlias: string,
  key: string,
  value: string,
): Promise<ScopedSecretMetadata> {
  debugLogger.info(
    "secrets-api",
    `Upserting project secret ${key} for ${projectAlias}`,
  );
  const api = new API(
    apiBaseUrls.BACKEND,
    `${PROJECT_BASE}/${projectAlias}/secrets/${encodeURIComponent(key)}`,
  );
  return api.put<
    ScopedSecretMetadata,
    { key: string; value: string; isSecret: boolean }
  >({
    key,
    value,
    isSecret: true,
  });
}

export async function revealProjectSecret(
  projectAlias: string,
  key: string,
): Promise<string> {
  debugLogger.info(
    "secrets-api",
    `Revealing project secret ${key} for ${projectAlias}`,
  );
  const api = new API(
    apiBaseUrls.BACKEND,
    `${PROJECT_BASE}/${projectAlias}/secrets/${encodeURIComponent(key)}/reveal`,
  );
  const result = await api.get<SecretRevealResponse>();
  return result.value;
}

export async function deleteProjectSecret(
  projectAlias: string,
  key: string,
): Promise<void> {
  debugLogger.info(
    "secrets-api",
    `Deleting project secret ${key} for ${projectAlias}`,
  );
  const api = new API(
    apiBaseUrls.BACKEND,
    `${PROJECT_BASE}/${projectAlias}/secrets/${encodeURIComponent(key)}`,
  );
  await api.delete<void>();
}

// ── Effective merged secrets (stack scope) ────────────────────────────────────

export async function getEffectiveSecrets(
  appAlias: string,
): Promise<EffectiveSecret[]> {
  debugLogger.info("secrets-api", `Fetching effective secrets for ${appAlias}`);
  const api = new API(
    apiBaseUrls.BACKEND,
    `${BASE}/${appAlias}/secrets/effective`,
  );
  return api.get<EffectiveSecret[]>();
}
